import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, 
  MapPin, 
  DollarSign, 
  Percent, 
  Users,
  Search,
  RefreshCw,
  Bookmark,
  BookmarkCheck,
  Eye,
  Clock,
  Home
} from 'lucide-react';
import API_CONSTRUCTOR from '../../models/api-model';
import { 
  formatCurrency, 
  formatPercentage, 
  getPropertyLocation,
  getFundingProgressColor 
} from '../../utils/investorUtils';
import { toast } from 'sonner';

export const Opportunities = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    city: '',
    minInvestment: '',
    maxInvestment: '',
    minReturnRate: ''
  });

  // Load opportunities
  const loadOpportunities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const api = API_CONSTRUCTOR();
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.minInvestment) params.append('minInvestment', filters.minInvestment);
      if (filters.maxInvestment) params.append('maxInvestment', filters.maxInvestment);
      if (filters.minReturnRate) params.append('minReturnRate', filters.minReturnRate);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await api.get(`/opportunities${queryString}`);
      
      setOpportunities(response.data || []);
    } catch (err) {
      console.error('Error loading opportunities:', err);
      setError(err.response?.data?.error || 'Failed to load opportunities');
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOpportunities();
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Apply filters
  const handleApplyFilters = () => {
    loadOpportunities();
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      city: '',
      minInvestment: '',
      maxInvestment: '',
      minReturnRate: ''
    });
  };

  // View opportunity detail
  const handleViewOpportunity = (queryId) => {
    navigate(`/opportunities/${queryId}`);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Investment Opportunities</h1>
        <p className="text-gray-600">
          Browse available properties and start investing today
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                placeholder="Enter city"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="minInvestment">Min Investment ($)</Label>
              <Input
                id="minInvestment"
                type="number"
                placeholder="e.g., 10000"
                value={filters.minInvestment}
                onChange={(e) => handleFilterChange('minInvestment', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="maxInvestment">Max Investment ($)</Label>
              <Input
                id="maxInvestment"
                type="number"
                placeholder="e.g., 100000"
                value={filters.maxInvestment}
                onChange={(e) => handleFilterChange('maxInvestment', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="minReturnRate">Min Return Rate (%)</Label>
              <Input
                id="minReturnRate"
                type="number"
                placeholder="e.g., 15"
                value={filters.minReturnRate}
                onChange={(e) => handleFilterChange('minReturnRate', e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleApplyFilters}>
              <Search className="h-4 w-4 mr-2" />
              Apply Filters
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear
            </Button>
            <Button variant="outline" onClick={loadOpportunities} className="ml-auto">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Opportunities List */}
      {opportunities.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-center">
            <Home className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No investment opportunities available at this time.</p>
            <p className="text-sm text-gray-500 mt-2">
              Check back later for new opportunities.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {opportunities.map((opportunity) => (
            <Card key={opportunity.id} className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-2">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    Available
                  </Badge>
                  <Bookmark className="h-5 w-5 text-gray-400 hover:text-primary cursor-pointer" />
                </div>
                <CardTitle className="text-lg line-clamp-2">
                  {opportunity.property_address || 'Property Address'}
                </CardTitle>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {getPropertyLocation(opportunity)}
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Investment Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Total Investment</p>
                    <p className="text-lg font-semibold text-primary">
                      {formatCurrency(opportunity.total_investment_base)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Expected Profit</p>
                    <p className="text-lg font-semibold text-green-600">
                      {formatCurrency(opportunity.investor_profit_total)}
                    </p>
                  </div>
                </div>

                {/* Return Rate & Duration */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Percent className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-muted-foreground">Return Rate</p>
                      <p className="text-sm font-medium">{opportunity.return_rate}% p.a.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-muted-foreground">Duration</p>
                      <p className="text-sm font-medium">{opportunity.rental_period} months</p>
                    </div>
                  </div>
                </div>

                {/* Funding Progress */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Funding Progress</span>
                    <span className="font-medium">{formatPercentage(opportunity.funding_percentage || 0)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${getFundingProgressColor(opportunity.funding_percentage || 0)}`}
                      style={{ width: `${Math.min(opportunity.funding_percentage || 0, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {opportunity.investor_count || 0} investor{(opportunity.investor_count || 0) !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* View Details Button */}
                <Button 
                  className="w-full" 
                  onClick={() => handleViewOpportunity(opportunity.id)}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Opportunities;
